import React, { useMemo, useRef, useState } from "react";

const sampleHtml = `<section>
  <h1>Hello developer</h1>
  <p>Edit the markup and preview it instantly.</p>
  <button>Ship it</button>
</section>`;

const safeBase64Encode = (value) => {
  try {
    return btoa(unescape(encodeURIComponent(value)));
  } catch (error) {
    return "Unable to encode this value.";
  }
};

const safeBase64Decode = (value) => {
  try {
    return decodeURIComponent(escape(atob(value)));
  } catch (error) {
    return "Invalid Base64 input.";
  }
};

const minifyMarkup = (value) =>
  value
    .replace(/>\s+</g, "><")
    .replace(/\s{2,}/g, " ")
    .trim();

const minifyCode = (value) =>
  value
    .replace(/\/\*[\s\S]*?\*\//g, "")
    .replace(/\/\/.*$/gm, "")
    .replace(/\s+/g, " ")
    .replace(/\s*([{}:;,=+\-*/<>()[\]])\s*/g, "$1")
    .trim();

const toSlug = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const sortLines = (value) =>
  value
    .split(/\r?\n/)
    .sort((a, b) => a.localeCompare(b))
    .join("\n");

const uniqueLines = (value) =>
  Array.from(new Set(value.split(/\r?\n/))).join("\n");

const countWords = (value) => value.trim().split(/\s+/).filter(Boolean).length;

const sampleJwt =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJhci1wb3J0Zm9saW8iLCJuYW1lIjoiQWJkdWwgUmFobWFuIiwiaWF0IjoxNzE2MjM5MDIyfQ.signature";

const loremSentences = [
  "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
  "Integer vitae velit non justo facilisis tristique.",
  "Praesent commodo augue at risus luctus, sit amet luctus massa viverra.",
  "Curabitur faucibus neque ac lectus luctus, sed congue nibh tincidunt.",
  "Suspendisse potenti, sed gravida libero fermentum at.",
  "Donec id sem a velit suscipit viverra in nec lacus.",
  "Aliquam erat volutpat, morbi posuere sapien sed turpis laoreet, non pretium risus porta.",
  "Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia curae.",
  "Phasellus at mi non odio efficitur venenatis.",
  "Nunc dignissim tellus a tortor tincidunt, vitae volutpat justo blandit.",
  "Etiam euismod urna at nisi pulvinar, vel pulvinar nibh porttitor.",
  "Sed vitae magna ac dolor tempor malesuada.",
  "Maecenas rutrum magna eu metus commodo, eu facilisis erat hendrerit.",
  "Vivamus bibendum mi et lacus tristique, at aliquam enim egestas.",
  "Aenean sit amet lacus nec justo congue feugiat.",
  "Proin sagittis nulla vitae tortor porta, sed blandit dui faucibus.",
];

const paragraphOptions = [
  ["one", "1 Para"],
  ["two", "2 Para"],
  ["list", "Para + List"],
  ["image", "Para + Image"],
  ["large", "Large Para"],
];

const makeLoremParagraph = (start = 0, sentenceCount = 5) =>
  Array.from({ length: sentenceCount }, (_, index) => loremSentences[(start + index) % loremSentences.length]).join(" ");

const makeParagraphContent = (variant, format) => {
  const asHtml = format === "html";
  const paragraphs = {
    one: [makeLoremParagraph(0, 5)],
    two: [makeLoremParagraph(0, 4), makeLoremParagraph(5, 4)],
    large: [makeLoremParagraph(0, 12)],
  };

  if (variant === "list") {
    const paragraph = makeLoremParagraph(0, 4);
    const listItems = [
      "First testing point with enough text to wrap on mobile layouts.",
      "Second placeholder item for spacing, bullets, and alignment checks.",
      "Third list item for scanning nested content areas.",
      "Fourth item to test longer repeated list content.",
    ];

    if (asHtml) {
      return `<p>${paragraph}</p>\n<ul>\n${listItems.map((item) => `  <li>${item}</li>`).join("\n")}\n</ul>`;
    }

    return `${paragraph}\n\n${listItems.map((item) => `- ${item}`).join("\n")}`;
  }

  if (variant === "image") {
    const paragraph = makeLoremParagraph(0, 5);
    const imageUrl = "https://picsum.photos/seed/testing-content/960/540";

    if (asHtml) {
      return `<figure>\n  <img src="${imageUrl}" alt="Lorem ipsum placeholder" />\n  <figcaption>Placeholder image caption for content testing.</figcaption>\n</figure>\n<p>${paragraph}</p>`;
    }

    return `Image: ${imageUrl}\nCaption: Placeholder image caption for content testing.\n\n${paragraph}`;
  }

  const selectedParagraphs = paragraphs[variant] ?? paragraphs.one;

  if (asHtml) {
    return selectedParagraphs.map((paragraph) => `<p>${paragraph}</p>`).join("\n");
  }

  return selectedParagraphs.join("\n\n");
};

const randomId = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

const decodeJwtPart = (value) => {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  return JSON.parse(safeBase64Decode(padded));
};

const generatePassword = (length = 18) => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*_-+=?";
  const values = new Uint32Array(length);
  window.crypto.getRandomValues(values);
  return Array.from(values, (value) => chars[value % chars.length]).join("");
};

const hashText = async (value, algorithm) => {
  const data = new TextEncoder().encode(value);
  const hash = await window.crypto.subtle.digest(algorithm, data);
  return Array.from(new Uint8Array(hash), (byte) => byte.toString(16).padStart(2, "0")).join("");
};

const hexToRgb = (hex) => {
  const clean = hex.replace("#", "").trim();
  if (!/^[0-9a-fA-F]{6}$/.test(clean)) {
    return null;
  }

  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return { r, g, b };
};

const rgbToHsl = ({ r, g, b }) => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
    if (max === gn) h = (bn - rn) / d + 2;
    if (max === bn) h = (rn - gn) / d + 4;
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
};

const ToolButton = ({ children, active = false, ...props }) => (
  <button
    type="button"
    className={`rounded-full px-4 py-2 text-sm font-medium transition ${
      active
        ? "bg-[var(--accent)] text-white shadow-lg shadow-[var(--accent-glow)]"
        : "border border-white/15 bg-white/10 text-skin-text hover:bg-white/15"
    }`}
    {...props}
  >
    {children}
  </button>
);

const tokenStyle = {
  tag: "text-pink-400",
  attr: "text-sky-300",
  string: "text-emerald-300",
  comment: "text-slate-500",
  punctuation: "text-slate-400",
  keyword: "text-violet-300",
  number: "text-amber-300",
  text: "text-slate-100",
};

const highlightHtml = (code) => {
  const output = [];
  const pattern = /(<!--[\s\S]*?-->)|(<\/?[a-zA-Z][^>]*?>)/g;
  let cursor = 0;
  let match;

  const pushText = (text, className = tokenStyle.text) => {
    if (text) output.push({ text, className });
  };

  while ((match = pattern.exec(code)) !== null) {
    pushText(code.slice(cursor, match.index));

    if (match[1]) {
      pushText(match[1], tokenStyle.comment);
    } else {
      const tag = match[2];
      const tagPattern = /(\/?[a-zA-Z][\w:-]*)|([a-zA-Z_:][\w:.-]*)(?=\s*=)|("[^"]*"|'[^']*')|([<>/=])/g;
      let tagCursor = 0;
      let tagMatch;

      while ((tagMatch = tagPattern.exec(tag)) !== null) {
        pushText(tag.slice(tagCursor, tagMatch.index), tokenStyle.text);

        if (tagMatch[1]) pushText(tagMatch[1], tokenStyle.tag);
        else if (tagMatch[2]) pushText(tagMatch[2], tokenStyle.attr);
        else if (tagMatch[3]) pushText(tagMatch[3], tokenStyle.string);
        else if (tagMatch[4]) pushText(tagMatch[4], tokenStyle.punctuation);

        tagCursor = tagPattern.lastIndex;
      }

      pushText(tag.slice(tagCursor), tokenStyle.text);
    }

    cursor = pattern.lastIndex;
  }

  pushText(code.slice(cursor));
  return output;
};

const highlightCode = (code, language) => {
  if (language === "HTML") {
    return highlightHtml(code);
  }

  const output = [];
  const pattern =
    /(\/\*[\s\S]*?\*\/|\/\/.*$|#[^\n]*$)|("(?:\\.|[^"])*"|'(?:\\.|[^'])*'|`(?:\\.|[^`])*`)|(true|false|null|undefined|const|let|var|return|function|if|else|for|while|class|import|export|from|async|await|new|try|catch|throw)\b|(\b\d+(?:\.\d+)?\b)|([{}()[\].,;:=+\-*/<>!?&|])/gm;
  let cursor = 0;
  let match;

  while ((match = pattern.exec(code)) !== null) {
    if (match.index > cursor) {
      output.push({ text: code.slice(cursor, match.index), className: tokenStyle.text });
    }

    if (match[1]) output.push({ text: match[1], className: tokenStyle.comment });
    else if (match[2]) output.push({ text: match[2], className: tokenStyle.string });
    else if (match[3]) output.push({ text: match[3], className: tokenStyle.keyword });
    else if (match[4]) output.push({ text: match[4], className: tokenStyle.number });
    else if (match[5]) output.push({ text: match[5], className: tokenStyle.punctuation });

    cursor = pattern.lastIndex;
  }

  if (cursor < code.length) {
    output.push({ text: code.slice(cursor), className: tokenStyle.text });
  }

  return output;
};

const CodeField = ({ label, value, onChange, placeholder, rows = 12, language = "TXT" }) => {
  const highlightRef = useRef(null);
  const gutterRef = useRef(null);
  const lineNumbers = value.split(/\r?\n/).map((_, index) => index + 1);
  const highlightedTokens = highlightCode(value, language);
  const editorHeight = "32rem";

  const syncEditorScroll = (event) => {
    if (highlightRef.current) {
      highlightRef.current.scrollTop = event.currentTarget.scrollTop;
      highlightRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }

    if (gutterRef.current) {
      gutterRef.current.scrollTop = event.currentTarget.scrollTop;
    }
  };

  return (
    <label className="group block overflow-hidden rounded-2xl border border-slate-700/80 bg-[#0d1117] shadow-[0_22px_70px_rgba(2,6,23,0.55)] transition focus-within:border-[var(--accent)] focus-within:shadow-[0_0_0_4px_var(--accent-glow)]">
      <div className="flex items-center justify-between border-b border-slate-700/80 bg-[#161b22] px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-full bg-[#ff5f57]" />
          <span className="h-3 w-3 rounded-full bg-[#ffbd2e]" />
          <span className="h-3 w-3 rounded-full bg-[#28c840]" />
        </div>
        <span className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
          {label}
        </span>
        <span className="rounded-full border border-slate-600/80 bg-slate-900 px-2.5 py-1 text-[0.65rem] font-semibold text-cyan-300">
          {language}
        </span>
      </div>
      <div className="grid grid-cols-[3.5rem_minmax(0,1fr)] bg-[#0d1117]">
        <div
          ref={gutterRef}
          aria-hidden="true"
          className="max-h-full select-none overflow-hidden border-r border-slate-800 bg-[#090d13] px-3 py-4 text-right font-mono text-sm leading-6 text-slate-600"
          style={{ height: editorHeight }}
        >
          {lineNumbers.map((line) => (
            <div key={line}>{line}</div>
          ))}
        </div>
        <div className="relative min-w-0 bg-[#0d1117]" style={{ height: editorHeight }}>
          <pre
            ref={highlightRef}
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 m-0 overflow-auto whitespace-pre p-4 font-mono text-sm leading-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            style={{ tabSize: 2 }}
          >
            {highlightedTokens.map((token, index) => (
              <span key={`${index}-${token.text}`} className={token.className}>
                {token.text}
              </span>
            ))}
          </pre>
          <textarea
            className="relative h-full w-full max-w-full resize-none overflow-auto border-0 bg-transparent p-4 font-mono text-sm leading-6 text-transparent caret-cyan-300 outline-none placeholder:text-slate-600 selection:bg-cyan-400/35"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onScroll={syncEditorScroll}
            placeholder={placeholder}
            rows={rows}
            spellCheck="false"
            wrap="off"
            style={{ tabSize: 2, WebkitTextFillColor: "transparent" }}
          />
        </div>
      </div>
    </label>
  );
};

const CodingTools = () => {
  const [activeTool, setActiveTool] = useState("strings");
  const [stringInput, setStringInput] = useState("Build fast. Refactor carefully.\nBuild fast.");
  const [stringOutput, setStringOutput] = useState("");
  const [leftCompare, setLeftCompare] = useState("const name = 'AR';\nreturn name;");
  const [rightCompare, setRightCompare] = useState("const fullName = 'AR';\nreturn fullName;");
  const [ignoreCase, setIgnoreCase] = useState(false);
  const [ignoreWhitespace, setIgnoreWhitespace] = useState(false);
  const [minifyInput, setMinifyInput] = useState(sampleHtml);
  const [minifyMode, setMinifyMode] = useState("html");
  const [minifyOutput, setMinifyOutput] = useState("");
  const [htmlInput, setHtmlInput] = useState(sampleHtml);
  const [jsonInput, setJsonInput] = useState('{"name":"AR","stack":["React","Laravel","Flutter"]}');
  const [jsonOutput, setJsonOutput] = useState("");
  const [urlInput, setUrlInput] = useState("https://abdrahman.in/?q=react tools&tab=html");
  const [urlOutput, setUrlOutput] = useState("");
  const [regexPattern, setRegexPattern] = useState("\\bReact\\b");
  const [regexFlags, setRegexFlags] = useState("gi");
  const [regexInput, setRegexInput] = useState("React tools help React developers move faster.");
  const [timestampInput, setTimestampInput] = useState(String(Math.floor(Date.now() / 1000)));
  const [colorInput, setColorInput] = useState("#06b6d4");
  const [jwtInput, setJwtInput] = useState(sampleJwt);
  const [hashInput, setHashInput] = useState("Hash this deployment note");
  const [hashOutput, setHashOutput] = useState("");
  const [hashAlgorithm, setHashAlgorithm] = useState("SHA-256");
  const [uuidOutput, setUuidOutput] = useState(randomId());
  const [passwordLength, setPasswordLength] = useState(18);
  const [passwordOutput, setPasswordOutput] = useState(generatePassword(18));
  const [pxValue, setPxValue] = useState("16");
  const [baseFontSize, setBaseFontSize] = useState("16");
  const [paragraphVariant, setParagraphVariant] = useState("one");
  const [paragraphFormat, setParagraphFormat] = useState("text");
  const [snippetInput, setSnippetInput] = useState("const apiBase = process.env.REACT_APP_API_URL;\nconsole.log(apiBase);");
  const [copyStatus, setCopyStatus] = useState("");

  const compareStats = useMemo(() => {
    const normalize = (value) => {
      let next = value;
      if (ignoreCase) next = next.toLowerCase();
      if (ignoreWhitespace) next = next.replace(/\s+/g, "");
      return next;
    };
    const left = normalize(leftCompare);
    const right = normalize(rightCompare);
    const leftLines = left.split(/\r?\n/);
    const rightLines = right.split(/\r?\n/);
    const maxLines = Math.max(leftLines.length, rightLines.length);
    const changedLines = Array.from({ length: maxLines }).filter(
      (_, index) => leftLines[index] !== rightLines[index]
    ).length;

    return {
      same: left === right,
      leftChars: leftCompare.length,
      rightChars: rightCompare.length,
      changedLines,
      maxLines,
    };
  }, [ignoreCase, ignoreWhitespace, leftCompare, rightCompare]);

  const regexMatches = useMemo(() => {
    try {
      const flags = regexFlags.includes("g") ? regexFlags : `${regexFlags}g`;
      const regex = new RegExp(regexPattern, flags);
      return {
        error: "",
        matches: Array.from(regexInput.matchAll(regex)).map((match) => ({
          text: match[0],
          index: match.index,
        })),
      };
    } catch (error) {
      return { error: error.message, matches: [] };
    }
  }, [regexFlags, regexInput, regexPattern]);

  const colorValue = useMemo(() => {
    const rgb = hexToRgb(colorInput);
    if (!rgb) {
      return null;
    }
    const hsl = rgbToHsl(rgb);
    return {
      rgb: `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`,
      hsl: `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`,
    };
  }, [colorInput]);

  const runStringAction = (action) => {
    const actions = {
      uppercase: stringInput.toUpperCase(),
      lowercase: stringInput.toLowerCase(),
      title: stringInput.replace(/\w\S*/g, (word) => word[0].toUpperCase() + word.slice(1).toLowerCase()),
      trim: stringInput.trim(),
      slug: toSlug(stringInput),
      reverse: stringInput.split("").reverse().join(""),
      sort: sortLines(stringInput),
      unique: uniqueLines(stringInput),
      base64Encode: safeBase64Encode(stringInput),
      base64Decode: safeBase64Decode(stringInput),
      urlEncode: encodeURIComponent(stringInput),
      urlDecode: (() => {
        try {
          return decodeURIComponent(stringInput);
        } catch (error) {
          return "Invalid URL encoded input.";
        }
      })(),
    };

    setStringOutput(actions[action] ?? "");
  };

  const runMinify = () => {
    if (minifyMode === "json") {
      try {
        setMinifyOutput(JSON.stringify(JSON.parse(minifyInput)));
      } catch (error) {
        setMinifyOutput(`Invalid JSON: ${error.message}`);
      }
      return;
    }

    setMinifyOutput(minifyMode === "html" ? minifyMarkup(minifyInput) : minifyCode(minifyInput));
  };

  const formatJson = (spaces = 2) => {
    try {
      setJsonOutput(JSON.stringify(JSON.parse(jsonInput), null, spaces));
    } catch (error) {
      setJsonOutput(`Invalid JSON: ${error.message}`);
    }
  };

  const copyText = async (value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopyStatus("Copied");
      window.setTimeout(() => setCopyStatus(""), 1200);
    } catch (error) {
      setCopyStatus("Copy failed");
    }
  };

  const timestampDate = useMemo(() => {
    const timestamp = Number(timestampInput);
    if (!Number.isFinite(timestamp)) {
      return "Invalid timestamp";
    }
    const milliseconds = timestampInput.length <= 10 ? timestamp * 1000 : timestamp;
    return new Date(milliseconds).toISOString();
  }, [timestampInput]);

  const jwtOutput = useMemo(() => {
    const parts = jwtInput.trim().split(".");
    if (parts.length < 2) {
      return "JWT must contain at least header and payload sections.";
    }

    try {
      return JSON.stringify(
        {
          header: decodeJwtPart(parts[0]),
          payload: decodeJwtPart(parts[1]),
          signature: parts[2] || "",
        },
        null,
        2
      );
    } catch (error) {
      return `Invalid JWT: ${error.message}`;
    }
  }, [jwtInput]);

  const cssUnits = useMemo(() => {
    const px = Number(pxValue);
    const base = Number(baseFontSize);
    if (!Number.isFinite(px) || !Number.isFinite(base) || base === 0) {
      return {
        rem: "Invalid input",
        em: "Invalid input",
        percent: "Invalid input",
      };
    }

    return {
      rem: `${Number((px / base).toFixed(4))}rem`,
      em: `${Number((px / base).toFixed(4))}em`,
      percent: `${Number(((px / base) * 100).toFixed(2))}%`,
    };
  }, [baseFontSize, pxValue]);

  const paragraphOutput = useMemo(
    () => makeParagraphContent(paragraphVariant, paragraphFormat),
    [paragraphFormat, paragraphVariant]
  );

  const tools = [
    ["strings", "String Lab"],
    ["compare", "Compare"],
    ["minify", "Minify"],
    ["html", "HTML Viewer"],
    ["json", "JSON"],
    ["url", "URL"],
    ["regex", "Regex"],
    ["time-color", "Time & Color"],
    ["jwt", "JWT"],
    ["hash", "Hash"],
    ["generators", "Generators"],
    ["css-units", "CSS Units"],
    ["paragraphs", "Paragraph Maker"],
    ["scratchpad", "Scratchpad"],
  ];

  return (
    <section className="rounded-[2rem] border border-white/15 bg-white/10 p-6 shadow-glass backdrop-blur-xl md:p-8">
      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.32em] text-skin-accentSoft">
            Coding Tools
          </p>
          <h1 className="font-display text-3xl font-bold text-skin-text md:text-4xl">
            Browser utilities for everyday development work
          </h1>
        </div>
        <p className="max-w-2xl text-sm leading-7 text-skin-muted">
          Transform strings, compare snippets, minify code, preview HTML, test regex,
          format JSON, encode URLs, and convert timestamps without leaving the portfolio.
        </p>
      </div>

      <div className="mb-8 flex flex-wrap gap-3">
        {tools.map(([key, label]) => (
          <ToolButton key={key} active={activeTool === key} onClick={() => setActiveTool(key)}>
            {label}
          </ToolButton>
        ))}
      </div>

      {copyStatus ? (
        <div className="mb-5 inline-flex rounded-full border border-white/15 bg-black/20 px-4 py-2 text-sm text-skin-accentSoft">
          {copyStatus}
        </div>
      ) : null}

      {activeTool === "strings" ? (
        <div className="grid gap-5 xl:grid-cols-2">
          <div className="rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
            <CodeField label="Input" value={stringInput} onChange={setStringInput} language="TXT" />
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {[
                ["uppercase", "Uppercase"],
                ["lowercase", "Lowercase"],
                ["title", "Title Case"],
                ["trim", "Trim"],
                ["slug", "Slug"],
                ["reverse", "Reverse"],
                ["sort", "Sort Lines"],
                ["unique", "Unique Lines"],
                ["base64Encode", "Base64 +"],
                ["base64Decode", "Base64 -"],
                ["urlEncode", "URL +"],
                ["urlDecode", "URL -"],
              ].map(([key, label]) => (
                <ToolButton key={key} onClick={() => runStringAction(key)}>
                  {label}
                </ToolButton>
              ))}
            </div>
          </div>
          <div className="rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
            <CodeField label="Output" value={stringOutput} onChange={setStringOutput} language="TXT" />
            <div className="mt-5 flex flex-wrap gap-3 text-sm text-skin-muted">
              <span>{stringInput.length} chars</span>
              <span>{countWords(stringInput)} words</span>
              <span>{stringInput.split(/\r?\n/).length} lines</span>
              <ToolButton onClick={() => copyText(stringOutput)}>Copy Output</ToolButton>
            </div>
          </div>
        </div>
      ) : null}

      {activeTool === "compare" ? (
        <div className="space-y-5">
          <div className="flex flex-wrap gap-3">
            <label className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm text-skin-text">
              <input type="checkbox" checked={ignoreCase} onChange={(event) => setIgnoreCase(event.target.checked)} />
              Ignore case
            </label>
            <label className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm text-skin-text">
              <input type="checkbox" checked={ignoreWhitespace} onChange={(event) => setIgnoreWhitespace(event.target.checked)} />
              Ignore whitespace
            </label>
          </div>
          <div className="grid gap-5 xl:grid-cols-2">
            <CodeField label="Left Text" value={leftCompare} onChange={setLeftCompare} language="JS" />
            <CodeField label="Right Text" value={rightCompare} onChange={setRightCompare} language="JS" />
          </div>
          <div className="grid gap-4 rounded-[1.75rem] border border-white/15 bg-black/10 p-5 text-sm text-skin-muted md:grid-cols-4">
            <strong className={compareStats.same ? "text-emerald-300" : "text-amber-300"}>
              {compareStats.same ? "Texts match" : "Texts differ"}
            </strong>
            <span>Left: {compareStats.leftChars} chars</span>
            <span>Right: {compareStats.rightChars} chars</span>
            <span>{compareStats.changedLines} / {compareStats.maxLines} lines changed</span>
          </div>
        </div>
      ) : null}

      {activeTool === "minify" ? (
        <div className="grid gap-5 xl:grid-cols-2">
          <div className="rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
            <div className="mb-5 flex flex-wrap gap-3">
              {["html", "css", "js", "json"].map((mode) => (
                <ToolButton key={mode} active={minifyMode === mode} onClick={() => setMinifyMode(mode)}>
                  {mode.toUpperCase()}
                </ToolButton>
              ))}
              <ToolButton onClick={runMinify}>Run</ToolButton>
            </div>
            <CodeField label="Source" value={minifyInput} onChange={setMinifyInput} language={minifyMode.toUpperCase()} />
          </div>
          <div className="rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
            <CodeField label="Minified Output" value={minifyOutput} onChange={setMinifyOutput} language={minifyMode.toUpperCase()} />
            <div className="mt-5 flex flex-wrap gap-3 text-sm text-skin-muted">
              <span>Saved {Math.max(0, minifyInput.length - minifyOutput.length)} chars</span>
              <ToolButton onClick={() => copyText(minifyOutput)}>Copy Output</ToolButton>
            </div>
          </div>
        </div>
      ) : null}

      {activeTool === "html" ? (
        <div className="grid gap-5 xl:grid-cols-2">
          <CodeField label="HTML" value={htmlInput} onChange={setHtmlInput} language="HTML" />
          <div className="rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
            <div className="mb-3 text-sm font-medium uppercase tracking-[0.2em] text-skin-accentSoft">
              Live Preview
            </div>
            <iframe
              title="HTML preview"
              className="h-[32rem] w-full rounded-2xl border border-white/15 bg-white"
              sandbox=""
              srcDoc={htmlInput}
            />
          </div>
        </div>
      ) : null}

      {activeTool === "json" ? (
        <div className="grid gap-5 xl:grid-cols-2">
          <div className="rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
            <div className="mb-5 flex flex-wrap gap-3">
              <ToolButton onClick={() => formatJson(2)}>Format</ToolButton>
              <ToolButton onClick={() => formatJson(0)}>Minify</ToolButton>
            </div>
            <CodeField label="JSON Input" value={jsonInput} onChange={setJsonInput} language="JSON" />
          </div>
          <div className="rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
            <CodeField label="JSON Output" value={jsonOutput} onChange={setJsonOutput} language="JSON" />
            <div className="mt-5">
              <ToolButton onClick={() => copyText(jsonOutput)}>Copy Output</ToolButton>
            </div>
          </div>
        </div>
      ) : null}

      {activeTool === "url" ? (
        <div className="grid gap-5 xl:grid-cols-2">
          <div className="rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
            <div className="mb-5 flex flex-wrap gap-3">
              <ToolButton onClick={() => setUrlOutput(encodeURIComponent(urlInput))}>Encode</ToolButton>
              <ToolButton
                onClick={() => {
                  try {
                    setUrlOutput(decodeURIComponent(urlInput));
                  } catch (error) {
                    setUrlOutput("Invalid encoded URL.");
                  }
                }}
              >
                Decode
              </ToolButton>
            </div>
            <CodeField label="URL Input" value={urlInput} onChange={setUrlInput} language="URL" />
          </div>
          <div className="rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
            <CodeField label="URL Output" value={urlOutput} onChange={setUrlOutput} language="URL" />
          </div>
        </div>
      ) : null}

      {activeTool === "regex" ? (
        <div className="space-y-5">
          <div className="grid gap-4 rounded-[1.75rem] border border-white/15 bg-black/10 p-5 md:grid-cols-[1fr_20rem]">
            <label className="grid gap-2 text-sm text-skin-muted">
              Pattern
              <input className="rounded-2xl border border-white/15 bg-black/20 px-4 py-3 font-mono text-skin-text outline-none" value={regexPattern} onChange={(event) => setRegexPattern(event.target.value)} />
            </label>
            <label className="grid gap-2 text-sm text-skin-muted">
              Flags
              <input className="rounded-2xl border border-white/15 bg-black/20 px-4 py-3 font-mono text-skin-text outline-none" value={regexFlags} onChange={(event) => setRegexFlags(event.target.value)} />
            </label>
          </div>
          <div className="grid gap-5 xl:grid-cols-2">
            <CodeField label="Test Text" value={regexInput} onChange={setRegexInput} language="REGEX" />
            <div className="rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
              <div className="mb-3 text-sm font-medium uppercase tracking-[0.2em] text-skin-accentSoft">
                Matches
              </div>
              {regexMatches.error ? (
                <p className="text-sm text-amber-300">{regexMatches.error}</p>
              ) : (
                <div className="space-y-3 text-sm text-skin-muted">
                  <p>{regexMatches.matches.length} matches found</p>
                  {regexMatches.matches.map((match, index) => (
                    <div key={`${match.index}-${index}`} className="rounded-2xl border border-white/10 bg-white/5 p-3 font-mono">
                      {match.text} <span className="text-skin-accentSoft">@ {match.index}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {activeTool === "time-color" ? (
        <div className="grid gap-5 xl:grid-cols-2">
          <div className="rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
            <label className="grid gap-3 text-sm text-skin-muted">
              <span className="uppercase tracking-[0.2em] text-skin-accentSoft">Unix Timestamp</span>
              <input className="rounded-2xl border border-white/15 bg-black/20 px-4 py-3 font-mono text-skin-text outline-none" value={timestampInput} onChange={(event) => setTimestampInput(event.target.value)} />
            </label>
            <div className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-4 font-mono text-sm text-skin-text">
              {timestampDate}
            </div>
          </div>
          <div className="rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
            <label className="grid gap-3 text-sm text-skin-muted">
              <span className="uppercase tracking-[0.2em] text-skin-accentSoft">Hex Color</span>
              <input className="rounded-2xl border border-white/15 bg-black/20 px-4 py-3 font-mono text-skin-text outline-none" value={colorInput} onChange={(event) => setColorInput(event.target.value)} />
            </label>
            <div className="mt-5 flex flex-col gap-4 md:flex-row md:items-center">
              <div className="h-20 w-20 rounded-2xl border border-white/15" style={{ background: colorInput }} />
              <div className="space-y-2 font-mono text-sm text-skin-text">
                <div>{colorValue?.rgb ?? "Invalid hex color"}</div>
                <div>{colorValue?.hsl ?? ""}</div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {activeTool === "jwt" ? (
        <div className="grid gap-5 xl:grid-cols-2">
          <div className="rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
            <CodeField label="JWT Token" value={jwtInput} onChange={setJwtInput} language="JWT" />
          </div>
          <div className="rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
            <CodeField label="Decoded Token" value={jwtOutput} onChange={() => {}} language="JSON" />
            <div className="mt-5">
              <ToolButton onClick={() => copyText(jwtOutput)}>Copy Decoded JSON</ToolButton>
            </div>
          </div>
        </div>
      ) : null}

      {activeTool === "hash" ? (
        <div className="grid gap-5 xl:grid-cols-2">
          <div className="rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
            <div className="mb-5 flex flex-wrap gap-3">
              {["SHA-256", "SHA-384", "SHA-512"].map((algorithm) => (
                <ToolButton
                  key={algorithm}
                  active={hashAlgorithm === algorithm}
                  onClick={() => setHashAlgorithm(algorithm)}
                >
                  {algorithm}
                </ToolButton>
              ))}
              <ToolButton
                onClick={async () => {
                  setHashOutput(await hashText(hashInput, hashAlgorithm));
                }}
              >
                Generate
              </ToolButton>
            </div>
            <CodeField label="Input" value={hashInput} onChange={setHashInput} language="TXT" />
          </div>
          <div className="rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
            <CodeField label="Hash Output" value={hashOutput} onChange={setHashOutput} language={hashAlgorithm} />
            <div className="mt-5">
              <ToolButton onClick={() => copyText(hashOutput)}>Copy Hash</ToolButton>
            </div>
          </div>
        </div>
      ) : null}

      {activeTool === "generators" ? (
        <div className="grid gap-5 xl:grid-cols-2">
          <div className="rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
            <div className="mb-5 flex flex-wrap gap-3">
              <ToolButton onClick={() => setUuidOutput(randomId())}>New UUID</ToolButton>
              <ToolButton onClick={() => copyText(uuidOutput)}>Copy UUID</ToolButton>
            </div>
            <CodeField label="UUID" value={uuidOutput} onChange={setUuidOutput} language="UUID" />
          </div>
          <div className="rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
            <div className="mb-5 grid gap-3 md:grid-cols-[1fr_auto_auto] md:items-end">
              <label className="grid gap-2 text-sm text-skin-muted">
                Password length
                <input
                  className="rounded-2xl border border-white/15 bg-black/20 px-4 py-3 font-mono text-skin-text outline-none"
                  min="8"
                  max="64"
                  type="number"
                  value={passwordLength}
                  onChange={(event) => setPasswordLength(Number(event.target.value))}
                />
              </label>
              <ToolButton onClick={() => setPasswordOutput(generatePassword(passwordLength))}>
                Generate
              </ToolButton>
              <ToolButton onClick={() => copyText(passwordOutput)}>Copy</ToolButton>
            </div>
            <CodeField label="Password" value={passwordOutput} onChange={setPasswordOutput} language="SECRET" />
          </div>
        </div>
      ) : null}

      {activeTool === "css-units" ? (
        <div className="grid gap-5 xl:grid-cols-2">
          <div className="rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
            <div className="grid gap-4 md:grid-cols-2">
              <label className="grid gap-2 text-sm text-skin-muted">
                Pixel value
                <input
                  className="rounded-2xl border border-white/15 bg-black/20 px-4 py-3 font-mono text-skin-text outline-none"
                  value={pxValue}
                  onChange={(event) => setPxValue(event.target.value)}
                />
              </label>
              <label className="grid gap-2 text-sm text-skin-muted">
                Base font size
                <input
                  className="rounded-2xl border border-white/15 bg-black/20 px-4 py-3 font-mono text-skin-text outline-none"
                  value={baseFontSize}
                  onChange={(event) => setBaseFontSize(event.target.value)}
                />
              </label>
            </div>
            <div className="mt-5 grid gap-4 text-sm text-skin-text md:grid-cols-3">
              {Object.entries(cssUnits).map(([label, value]) => (
                <div key={label} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="text-xs uppercase tracking-[0.22em] text-skin-accentSoft">{label}</div>
                  <div className="mt-2 font-mono text-lg">{value}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
            <CodeField
              label="CSS Output"
              value={`font-size: ${cssUnits.rem};\nmargin: ${cssUnits.em};\nwidth: ${cssUnits.percent};`}
              onChange={() => {}}
              language="CSS"
            />
          </div>
        </div>
      ) : null}

      {activeTool === "paragraphs" ? (
        <div className="grid gap-5 xl:grid-cols-[22rem_minmax(0,1fr)]">
          <div className="rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
            <div className="mb-6">
              <div className="mb-3 text-sm font-medium uppercase tracking-[0.2em] text-skin-accentSoft">
                Sections
              </div>
              <div className="grid gap-3">
                {paragraphOptions.map(([key, label]) => (
                  <ToolButton
                    key={key}
                    active={paragraphVariant === key}
                    onClick={() => setParagraphVariant(key)}
                  >
                    {label}
                  </ToolButton>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <div className="mb-3 text-sm font-medium uppercase tracking-[0.2em] text-skin-accentSoft">
                Format
              </div>
              <div className="flex flex-wrap gap-3">
                <ToolButton
                  active={paragraphFormat === "text"}
                  onClick={() => setParagraphFormat("text")}
                >
                  Plain Text
                </ToolButton>
                <ToolButton
                  active={paragraphFormat === "html"}
                  onClick={() => setParagraphFormat("html")}
                >
                  HTML
                </ToolButton>
              </div>
            </div>

            <div className="grid gap-3 text-sm text-skin-muted">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <span className="block text-xs uppercase tracking-[0.22em] text-skin-accentSoft">
                  Output
                </span>
                <span className="mt-2 block">{paragraphOutput.length} chars</span>
                <span>{countWords(paragraphOutput)} words</span>
              </div>
              <ToolButton onClick={() => copyText(paragraphOutput)}>Copy Content</ToolButton>
            </div>
          </div>

          <div className="grid gap-5">
            <div className="rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
              <CodeField
                label="Generated Content"
                value={paragraphOutput}
                onChange={() => {}}
                language={paragraphFormat === "html" ? "HTML" : "TXT"}
              />
            </div>

            <div className="rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
              <div className="mb-3 text-sm font-medium uppercase tracking-[0.2em] text-skin-accentSoft">
                Preview
              </div>
              {paragraphFormat === "html" ? (
                <iframe
                  title="Paragraph preview"
                  className="h-80 w-full rounded-2xl border border-white/15 bg-white"
                  sandbox=""
                  srcDoc={`<!doctype html><html><head><style>body{font-family:Arial,sans-serif;line-height:1.65;color:#111827;padding:24px;margin:0;}img{max-width:100%;border-radius:12px;}figure{margin:0 0 20px;}figcaption{margin-top:8px;color:#6b7280;font-size:14px;}li{margin-bottom:8px;}</style></head><body>${paragraphOutput}</body></html>`}
                />
              ) : (
                <div className="max-h-80 overflow-auto whitespace-pre-wrap rounded-2xl border border-white/10 bg-white/5 p-5 text-sm leading-7 text-skin-text">
                  {paragraphOutput}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {activeTool === "scratchpad" ? (
        <div className="rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
          <div className="mb-5 flex flex-wrap gap-3">
            <ToolButton onClick={() => copyText(snippetInput)}>Copy Snippet</ToolButton>
            <ToolButton onClick={() => setSnippetInput("")}>Clear</ToolButton>
          </div>
          <CodeField label="Snippet Scratchpad" value={snippetInput} onChange={setSnippetInput} language="JS" />
        </div>
      ) : null}
    </section>
  );
};

export default CodingTools;
